/**
 * Buyer Profile Agent: Custom hooks for buyer profile management.
 * Handles profile creation, preferences, and budget status.
 */

import { useState, useCallback } from 'react';
import APIAgent from './apiAgent';

/**
 * Hook for managing buyer profile
 * @returns Object with profile state, loading, error, and methods
 */
export const useBuyerProfile = (buyerId) => {
  const [profile, setProfile] = useState(null);
  const [preferences] = useState(null);
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch buyer profile
   */
  const fetchProfile = useCallback(async () => {
    if (!buyerId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(`/api/buyers/${buyerId}/profile`, 'GET');
      setProfile(data);
    } catch (err) {
      // A buyer who hasn't set up a readiness profile yet is the expected
      // empty/onboarding state, not an error — mirror useBuyerPreferences so
      // the hub renders "Set up your Buyer Readiness" instead of a red banner
      // (and don't spam the console for an expected 404).
      const message = err?.message || '';
      const status = err?.status ?? err?.statusCode;
      if (status === 404 || message.toLowerCase().includes('profile not found')) {
        setProfile(null);
        setError(null);
        return;
      }
      console.error('Error fetching profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  /**
   * Create buyer profile
   */
  const createProfile = useCallback(async (profileData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request('/api/buyers/profile', 'POST', {
        buyer_id: buyerId,
        ...profileData,
      });
      setProfile(data);
      return data;
    } catch (err) {
      console.error('Error creating profile:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  /**
   * Update buyer profile
   */
  const updateProfile = useCallback(async (profileData) => {
    if (!buyerId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/buyers/${buyerId}/profile`,
        'PUT',
        profileData
      );
      setProfile(data);
      return data;
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  /**
   * Fetch budget status
   */
  const fetchBudgetStatus = useCallback(async () => {
    if (!buyerId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/buyers/${buyerId}/budget-status`,
        'GET'
      );
      setBudgetStatus(data);
    } catch (err) {
      console.error('Error fetching budget status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  return {
    profile,
    preferences,
    budgetStatus,
    loading,
    error,
    fetchProfile,
    createProfile,
    updateProfile,
    fetchBudgetStatus,
  };
};

/**
 * Hook for managing buyer preferences
 */
export const useBuyerPreferences = (buyerId) => {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch buyer preferences
   */
  const fetchPreferences = useCallback(async () => {
    if (!buyerId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/buyers/${buyerId}/preferences`,
        'GET'
      );
      setPreferences(data);
    } catch (err) {
      const message = err?.message || '';
      if (message.toLowerCase().includes('preferences not found')) {
        setPreferences(null);
        setError(null);
        return;
      }
      console.error('Error fetching preferences:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  /**
   * Create buyer preferences
   */
  const createPreferences = useCallback(async (prefData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/buyers/${buyerId}/preferences`,
        'POST',
        prefData
      );
      setPreferences(data);
      return data;
    } catch (err) {
      console.error('Error creating preferences:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  /**
   * Update buyer preferences
   */
  const updatePreferences = useCallback(async (prefData) => {
    if (!buyerId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await APIAgent.request(
        `/api/buyers/${buyerId}/preferences`,
        'PUT',
        prefData
      );
      setPreferences(data);
      return data;
    } catch (err) {
      console.error('Error updating preferences:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  return {
    preferences,
    loading,
    error,
    fetchPreferences,
    createPreferences,
    updatePreferences,
  };
};
